/**
 * Shared domain primitives for the uploader subsystem.
 *
 * Pure, isomorphic types with zero runtime dependencies — importable from both
 * the server (re-running inference/validation over a scrubbed file) and the
 * client (live mapping UI, AI-prompt input assembly). The Standard-field
 * catalog (see ./catalog) is the single source of truth built on these.
 */

/**
 * Inferred / durable column types. `date` covers calendar dates AND period codes
 * (e.g. a `YYYYMM` snapshot month); Yes/No-style columns ride `categorical` (there
 * is deliberately no boolean type — real exports are too inconsistent to normalize).
 * A field's DURABLE type (its catalog/registry {@link StandardFieldDefinition.valueType})
 * is one of these and decides how the value is stored and sliced.
 */
export const INFERRED_TYPES = ['numeric', 'date', 'categorical', 'text'] as const;
export type InferredType = (typeof INFERRED_TYPES)[number];

/**
 * Refinements of a `numeric` field. For `number` and `currency` the subtype is
 * purely display — it changes ONLY how a stored value is rendered, never how it is
 * parsed or stored — so a `number <-> currency` change needs no data backfill.
 *
 * The two PERCENT variants are different (v7): the chosen variant drives the
 * AUTHORITATIVE, type-aware parse at PROJECTION. Canonical storage for a percent
 * field is percent-points (`8.36` ⇔ `8.36%`), so a `percentage` (fraction) column is
 * scaled ×100 into percent-points when it is projected, while a `percent_points`
 * column passes through unchanged. Both variants therefore render the SAME way —
 * `value + '%'`, no display-time ×100 — and differ only in what the projection stored.
 * A subtype change that crosses the `percentage` boundary changes stored values and
 * re-projects (see `percentScalingChanges` + `dbRetypeField`).
 *
 * The renderings:
 *   - `number` — a grouped decimal (`1,234.5`).
 *   - `currency` — Intl currency (`$1,234.56`).
 *   - `percentage` — the FRACTION percent variant (UI label "Percent (0.01)"): a
 *     %-decorated / fraction column, scaled ×100 into percent-points at projection.
 *   - `percent_points` — the PERCENT-POINTS variant (UI label "Percent (1%)"): a
 *     column already in percent-points (typical rate exports), stored unchanged.
 *   Both render the stored percent-points value with a `%` appended (5 → "5%").
 *
 * Between the two percent variants, `defaultPercentVariant` (./type-inference)
 * encodes the smart default from a column's max stored value.
 * `date`/`categorical`/`text` carry no subtype today (null); the column is reserved
 * for future refinements.
 */
export const NUMERIC_SUBTYPES = ['number', 'currency', 'percentage', 'percent_points'] as const;
export type NumericSubtype = (typeof NUMERIC_SUBTYPES)[number];
/** A field's display subtype, or null when it has none (non-numeric, or unspecified). */
export type ValueSubtype = NumericSubtype | null;

/** The two percent display variants: stored-fraction vs. stored-percent-points. */
export type PercentSubtype = Extract<NumericSubtype, 'percentage' | 'percent_points'>;

/** True when a subtype is one of the two percent display variants. */
export function isPercentSubtype(subtype: ValueSubtype): subtype is PercentSubtype {
  return subtype === 'percentage' || subtype === 'percent_points';
}

/**
 * Whether moving from `before` to `after` changes the value STORED at projection.
 * Canonical percent storage is percent-points, so projection scales x100 ONLY under
 * the fraction `percentage` variant; every other subtype passes the bare numeral
 * through. The stored value therefore changes exactly when `percentage` is on one
 * side and not the other (`percent_points <-> percentage`, or `percentage <->
 * number/currency`); a `number <-> currency` or `percent_points <-> number/currency`
 * move is display-only. Shared client<->server so the mapping/Fields-page UI and the
 * retype path agree on which subtype change is data-affecting (must recompute).
 */
export function percentScalingChanges(before: ValueSubtype, after: ValueSubtype): boolean {
  return (before === 'percentage') !== (after === 'percentage');
}

/**
 * The product file types — the semantic role a customer declares for an uploaded
 * file group. An upload is a coordinated batch of up to three groups:
 *   - `snapshot`    — portfolio snapshots at a point in time.
 *   - `origination` — original loan terms/balances (optional).
 *   - `transaction` — charge-off / recovery transactions (optional).
 * The customer declares the role by choosing a group card; the product never
 * infers it. An upload can contain any non-empty combination of these groups.
 */
export const FILE_TYPES = ['origination', 'snapshot', 'transaction'] as const;
export type FileType = (typeof FILE_TYPES)[number];

/**
 * One entry in the Standard-field catalog. `key` is widened to `string` here;
 * the catalog declares its entries `as const satisfies readonly
 * StandardFieldDefinition[]`, and `StandardFieldKey` (in ./catalog) extracts the
 * exact literal union from that const so the registry can never drift.
 */
export interface StandardFieldDefinition {
  /** Stable internal key — the join point across inference, validation, UI, AI. */
  readonly key: string;
  /**
   * The field's canonical VALUE type — how an accepted value is stored and sliced
   * in the portfolio projection: `numeric` → `numericValue`; `date` → a `YYYYMMDD`
   * integer ordinal in `numericValue` (with the display string in `valueText`);
   * `categorical` / `text` → `valueText`. Distinct from {@link acceptableTypes}
   * (the compat set used only for inference warnings). For custom fields the durable
   * registry type plays this role.
   */
  readonly valueType: InferredType;
  /**
   * The numeric field's canonical value SUBTYPE ({@link NumericSubtype}). Optional:
   * numeric fields without it default to `number` (see `getStandardFieldSubtype`);
   * non-numeric fields omit it. For the `percent_points`/`percentage` variants the
   * subtype now drives the authoritative parse at projection (a `percentage` column
   * is scaled ×100 into canonical percent-points storage), not just display.
   * `interest_rate` is `percent_points`: rates are stored as bare percent-points
   * (e.g. `8.36` ⇔ `8.36%`).
   */
  readonly valueSubtype?: NumericSubtype;
  /**
   * True when the org may override this standard field's percent variant
   * (`percentage` ⇔ `percent_points`) — clues pick the default, the customer's
   * choice is authoritative and stored org-scoped (registry subtype override).
   * Drives the mapping-screen + Fields-page variant control. Omitted means false.
   */
  readonly percentConfigurable?: boolean;
  /** Human label for the mapping UI + AI prompt. */
  readonly label: string;
  /** One-line product semantics — feeds the AI mapping prompt + UI tooltip. */
  readonly description: string;
  /**
   * Inferred types acceptable for this field. A mapped column whose inferred
   * type is NOT in this set gets a non-blocking type warning and is unusable
   * for modeling-readiness validation.
   */
  readonly acceptableTypes: readonly InferredType[];
  /**
   * Which file type(s) this field legitimately belongs to — the set of file
   * types whose mapping UI offers it and whose scope admits it for modeling.
   * (Replaces the old single `origination | snapshot | both` scalar now that
   * there are three file types and a field may belong to an arbitrary subset,
   * e.g. the charge-off fields belong to `snapshot` AND `transaction`.)
   */
  readonly scope: readonly FileType[];
  /**
   * Preferred field per spec "Preferred Standard Fields": shown even though it
   * does not block readiness; carries a specific non-blocking missing-warning.
   */
  readonly preferred: boolean;
  /**
   * True for fields the product recognizes but that play NO role in
   * modeling-readiness (e.g. fico_score). Shown in the Standard group, never
   * referenced by any minimum field set.
   */
  readonly recognizedNonModeling: boolean;
  /**
   * EVENT-SHAPED: absence of this field on a loan means "the event has not
   * happened to this loan", NOT "data is missing" (a performing loan simply has
   * no charge-off row; a live loan has no closed date). Readiness therefore
   * treats these as PORTFOLIO-LEVEL signals — a loan is ready when the signal
   * exists anywhere in the data, not only on its own rows — and any coverage
   * display phrases them as INCIDENCE ("31% of loans have a recorded
   * charge-off"), never as "missing". Measurement fields every loan carries
   * (balances, schedules, loan_status, terms) are NOT event-shaped and stay
   * per-loan. Omitted means false.
   */
  readonly eventShaped?: boolean;
  /**
   * Optional alternate header spellings, used only by the deterministic fallback
   * matcher when AI mapping suggestions are unavailable. Lowercased, no
   * separators (matching is normalized).
   */
  readonly aliases?: readonly string[];
}

/**
 * A raw source-column header from an uploaded file. Branded so it can't be
 * accidentally interchanged with a `StandardFieldKey` at call sites.
 */
export type SourceColumnName = string & { readonly __brand: 'SourceColumnName' };
